import type { RouterConfig } from '@nuxt/schema'

// A page that is still filling in (client-only blocks, lists) is shorter than the position we want to go back to,
// and the browser clamps a scroll past the end. Wait until it is tall enough, but not forever.
const RESTORE_WAIT_MS = 1500

export default {
  scrollBehavior: (to, from, savedPosition) => {
    // Reference: https://router.vuejs.org/guide/advanced/scroll-behavior.html#Scroll-Behavior
    // Staying on the same page and only changing the query string (e.g. the agenda syncing its
    // active filters to the URL) must NOT jump to the top — that scroll-to-top was being read by the
    // agenda's scroll-spy and resetting the selected day. Keep the current position in that case.
    if(to.path === from.path) {
      return false
    }

    // Already at the top (clientNavigation.global.ts) and restoring itself once mounted.
    if(SELF_RESTORING_PAGES.has(String(to.name))) {
      return false
    }

    // Back (and forward) to a page that was left scrolled: put it back where it was, as iOS shows it during the swipe.
    // Landing at the top instead is the jump people see right after the swipe finishes.
    if(savedPosition && savedPosition.top > 0) {
      const nuxtApp = useNuxtApp()
      return new Promise((resolve) => {
        const deadline = performance.now() + RESTORE_WAIT_MS
        const settle = () => {
          // Tapped on to another page while this one was still filling in: that page starts at the top.
          if(nuxtApp.$router.currentRoute.value.fullPath !== to.fullPath) {
            resolve(false)
            return
          }
          const tallEnough = document.documentElement.scrollHeight >= savedPosition.top + window.innerHeight
          if(tallEnough || performance.now() > deadline) {
            resolve({ left: 0, top: savedPosition.top, behavior: 'instant' })
            return
          }
          // A timer, not a frame: iOS runs no frames for about half a second after a swipe back.
          setTimeout(settle, 16)
        }
        // The pages swap without waiting for a frame (app.vue), so by 'page:finish' the new page is the one in the
        // document, and the next tick scrolls it before anything is painted.
        // (with a timeout in case it never reports; a second settle after the first resolved changes nothing)
        nuxtApp.hooks.hookOnce('page:finish', () => nextTick(settle))
        setTimeout(settle, RESTORE_WAIT_MS)
      })
    }

    return { top: 0 }
  }
} satisfies RouterConfig
